'use client';
import { useState, useEffect } from 'react';
import Link from 'next/link';
import Label from '@/components/ui/Label';
import { Story } from '@/lib/types';

interface HeroSliderProps {
  stories: Story[];
}

export default function HeroSlider({ stories }: HeroSliderProps) {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (stories.length <= 1) return;
    
    const timer = setInterval(() => {
      nextSlide();
    }, 6000);
    
    return () => clearInterval(timer);
  }, [current, stories.length]);

  const nextSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrent((prev) => (prev + 1) % stories.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  const prevSlide = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setCurrent((prev) => (prev - 1 + stories.length) % stories.length);
    setTimeout(() => setIsAnimating(false), 500);
  };

  if (!stories || stories.length === 0) return null;

  const story = stories[current];

  return (
    <div className="relative group">
      {/* Image Section */}
      <div className="relative overflow-hidden">
        <Link href={`/article/${story.slug}`}>
          <div 
            className={`${story.imageBg?.startsWith('bg-') ? story.imageBg : 'bg-gray-100'} h-[280px] lg:h-[340px] w-full mb-4 bg-cover bg-center bg-no-repeat transition-all duration-700 ease-in-out transform ${isAnimating ? 'opacity-80 scale-[1.02]' : 'opacity-100 scale-100'}`} 
            style={!story.imageBg?.startsWith('bg-') ? { backgroundImage: `url(${story.imageBg})` } : {}}
          />
        </Link>

        {/* Navigation Arrows */}
        {stories.length > 1 && (
          <>
            <button 
              onClick={(e) => { e.preventDefault(); prevSlide(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 border border-rule circle-avatar flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red hover:text-white hover:border-red z-20"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
            </button>
            <button 
              onClick={(e) => { e.preventDefault(); nextSlide(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 border border-rule circle-avatar flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red hover:text-white hover:border-red z-20"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
            </button>
          </>
        )}

        {/* Progress Dots */}
        {stories.length > 1 && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-20">
            {stories.map((_, i) => (
              <button 
                key={i} 
                onClick={() => setCurrent(i)}
                className={`w-2 h-2 circle-avatar transition-all ${i === current ? 'bg-red w-6' : 'bg-white/60 hover:bg-white'}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className={`transition-all duration-500 ${isAnimating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}>
        <div className="mb-2 flex items-center gap-2">
          <Label type={story.label || 'breaking'} animate={story.isLive} />
          <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px] text-[#6B6B6B]">Updated {story.publishedAt}</span>
        </div>
        <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[11px] tracking-[0.12em] uppercase text-[#C8102E]">{story.section}</span>
        <Link href={`/article/${story.slug}`}>
          <h1 className="font-['Playfair_Display',Georgia,serif] font-bold text-[28px] lg:text-[38px] leading-[1.15] text-[#0D0D0D] mt-1 mb-3 cursor-pointer hover:text-[#C8102E] transition-colors line-clamp-2">
            {story.title}
          </h1>
        </Link>
        <p className="font-['Source_Serif_4',Georgia,serif] text-[16px] leading-[1.55] text-[#2D2D2D] mb-3 line-clamp-3">
          {story.deck}
        </p>
        <div className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B]">
          By <span className="font-semibold text-[#0D0D0D]">{story.author}</span>
        </div>
      </div>
    </div>
  );
}
